import React, { useState, useEffect } from 'react'
import { differenceInCalendarDays } from 'date-fns'

// Compact hero: mobile-first header with league identity and primary action
export default function LeagueLandingHero({
  leagueName,
  divisionName,
  daysRemaining,
  leagueStartDate,
  leagueEndDate,
  unscheduledCount,
  homeCourtText,
  onEditHomeCourt,
  onViewOpponentList,
  onViewWeekly,
  onViewMonthly,
  onStandings,
  onMatches,
  onShowExplanation,
  onAddAvailability,
}) {
  const [showHomeCourtTooltip, setShowHomeCourtTooltip] = useState(false)

  // Close tooltip when clicking outside
  useEffect(() => {
    if (!showHomeCourtTooltip) return

    const handleClickOutside = (e) => {
      if (!e.target.closest('.ll-home-court-icon-wrapper')) {
        setShowHomeCourtTooltip(false)
      }
    }

    document.addEventListener('click', handleClickOutside)
    return () => document.removeEventListener('click', handleClickOutside)
  }, [showHomeCourtTooltip])

  // Calculate progress percentage for progress bar
  const getProgressInfo = () => {
    // If we don't have days remaining, show no progress (league may not have started or ended)
    if (daysRemaining === null) {
      return {
        percent: 0,
        daysText: 'Active',
      }
    }

    // Calculate actual league duration from start/end dates if available
    let totalDays = 90 // Default fallback
    if (leagueStartDate && leagueEndDate) {
      try {
        const start = new Date(leagueStartDate)
        const end = new Date(leagueEndDate)
        totalDays = differenceInCalendarDays(end, start)
        // Validate that totalDays is positive to prevent division issues
        if (totalDays <= 0) {
          totalDays = 90
        }
      } catch (e) {
        // Fall back to default if date parsing fails
        console.warn('Failed to parse league dates:', e)
      }
    }

    const daysElapsed = totalDays - daysRemaining
    const progressPercent = Math.max(
      0,
      Math.min(100, (daysElapsed / totalDays) * 100),
    )

    return {
      percent: progressPercent,
      daysText: `${daysRemaining} days left`,
    }
  }

  const progressInfo = getProgressInfo()

  return (
    <>
      {/* Status section - header with inline metadata */}
      <section className="ll-status-section mb-3" aria-label="League overview">
        {/* Title row with inline metadata */}
        <div className="ll-status-header">
          <div className="ll-status-title-group">
            <h1 className="ll-status-title">{leagueName}</h1>
            <span className="ll-status-meta">
              {divisionName}
              {/* Home court icon next to division name */}
              {homeCourtText && (
                <span
                  className="ll-home-court-icon-wrapper"
                  onMouseEnter={() => setShowHomeCourtTooltip(true)}
                  onMouseLeave={() => setShowHomeCourtTooltip(false)}
                  onClick={() => setShowHomeCourtTooltip(!showHomeCourtTooltip)}
                  role="button"
                  tabIndex={0}
                  aria-label={`Home court: ${homeCourtText}`}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault()
                      setShowHomeCourtTooltip(!showHomeCourtTooltip)
                    } else if (e.key === 'Escape') {
                      e.preventDefault()
                      setShowHomeCourtTooltip(false)
                    }
                  }}
                >
                  <span className="ll-home-court-icon" aria-hidden="true">
                    🏠
                  </span>
                  {showHomeCourtTooltip && (
                    <span className="ll-home-court-tooltip">
                      <strong>Home Court:</strong> {homeCourtText}
                      {onEditHomeCourt && (
                        <button
                          className="ll-home-court-edit-btn"
                          onClick={(e) => {
                            e.stopPropagation()
                            setShowHomeCourtTooltip(false)
                            onEditHomeCourt()
                          }}
                          aria-label="Change home court"
                        >
                          <span aria-hidden="true">✏️</span> Change
                        </button>
                      )}
                    </span>
                  )}
                </span>
              )}
            </span>
            <span className="ll-status-meta ll-status-meta-secondary">
              {progressInfo.daysText}
            </span>
          </div>
        </div>

        {/* Progress bar */}
        {daysRemaining !== null && (
          <div className="ll-status-progress">
            <div
              className="ll-progress-fill"
              style={{ width: `${progressInfo.percent}%` }}
              role="progressbar"
              aria-valuenow={Math.round(progressInfo.percent)}
              aria-valuemin="0"
              aria-valuemax="100"
              aria-label="League timeline progress"
            />
          </div>
        )}

        {/* System notice - inline warning without container */}
        {unscheduledCount > 0 && (
          <>
            <hr className="ll-status-divider" />
            <div className="ll-status-notice" role="status" aria-live="polite">
              ⚠️ {unscheduledCount} unscheduled match
              {unscheduledCount !== 1 ? 'es' : ''}
            </div>
          </>
        )}
      </section>

      {/* Primary CTA - Availability Actions (three distinct views) */}
      <section className="ll-primary-cta-section mb-3">
        <div className="d-flex flex-column gap-2">
          {/* Quick add button - positioned at top with section label */}
          {onAddAvailability && (
            <div className="d-flex justify-content-between align-items-center mb-2">
              <span className="ll-section-label">Availability</span>
              <button
                className="ll-btn-add"
                onClick={onAddAvailability}
                title="Add availability"
                aria-label="Add new availability"
              >
                <span aria-hidden="true">+</span>
              </button>
            </div>
          )}
          {/* Opponent list button - full width on mobile, joins others on desktop */}
          <div className="d-flex justify-content-center">
            <button
              className="ll-btn ll-btn-primary w-100 w-md-auto"
              style={{ fontSize: '0.9rem', padding: '0.5rem 1rem' }}
              onClick={onViewOpponentList}
              title="View and compare availability across all opponents"
            >
              <span className="d-none d-md-inline">
                👥 View Opponent Availability (List)
              </span>
              <span className="d-md-none">👥 Opponent Availability</span>
            </button>
          </div>
          {/* Calendar buttons - side by side on mobile and desktop */}
          <div className="d-flex gap-2 justify-content-center">
            <button
              className="ll-btn ll-btn-primary flex-fill flex-md-grow-0"
              style={{ fontSize: '0.9rem', padding: '0.5rem 1rem' }}
              onClick={onViewWeekly}
              title="View weekly calendar for short-term scheduling"
            >
              <span className="d-none d-md-inline">
                📅 View Weekly Calendar
              </span>
              <span className="d-md-none">📅 Weekly</span>
            </button>
            <button
              className="ll-btn ll-btn-primary flex-fill flex-md-grow-0"
              style={{ fontSize: '0.9rem', padding: '0.5rem 1rem' }}
              onClick={onViewMonthly}
              title="View monthly calendar for longer-term planning"
            >
              <span className="d-none d-md-inline">
                📆 View Monthly Calendar
              </span>
              <span className="d-md-none">📆 Monthly</span>
            </button>
          </div>
        </div>
      </section>

      {/* Secondary actions moved below header */}
      <section
        className="ll-secondary-actions mb-4"
        aria-label="Additional actions"
      >
        <div className="d-flex flex-wrap gap-2 justify-content-center">
          <button
            className="ll-btn ll-btn-outline"
            onClick={onStandings}
            title="View current standings"
          >
            🏆 Standings
          </button>
          <button
            className="ll-btn ll-btn-outline"
            onClick={onMatches}
            title="View all matches in this league"
          >
            🎾 All Matches
          </button>
          {onShowExplanation && (
            <button
              className="ll-btn ll-btn-outline"
              onClick={onShowExplanation}
              title="Learn how ServeSync works"
            >
              ❓ How It Works
            </button>
          )}
        </div>
      </section>
    </>
  )
}
